import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormGroup, ValidatorFn, UntypedFormBuilder, Validators } from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog as MatDialog,
} from '@angular/material/legacy-dialog';
import { Observable, of } from 'rxjs';
import { ModalConfirmationComponent } from 'src/app/shared/components/modal-confirmation/modal-confirmation.component';

@Component({
  selector: 'app-editable-labeled-simple-text',
  templateUrl: './editable-labeled-simple-text.component.html',
  styleUrls: ['./editable-labeled-simple-text.component.scss'],
})
export class EditableLabeledSimpleTextComponent implements OnInit {
  public isEditMode: boolean;
  editForm: UntypedFormGroup;
  private _description: string;
  private _validators: Array<ValidatorFn> = [];
  private _validatorsSub: Observable<Array<ValidatorFn>> = of([]);
  private confirmationDialogRef: MatDialogRef<ModalConfirmationComponent>;

  @Input()
  public readOnly: boolean = false;
  @Input()
  public showCreateInReadMode: boolean;
  @Input()
  public showDeleteInReadMode: boolean;
  @Input()
  public fieldName: string = 'field';
  @Input()
  public set description(value: string) {
    this._description = value;
    this.toggleEditMode(false);
  }
  public get description(): string {
    return this._description;
  }
  @Input()
  public label: string;
  @Input()
  public subText: string;
  @Input()
  public showSmallIcons: boolean = false;
  @Input()
  public set validators(obs: Observable<Array<ValidatorFn>>) {
    this._validatorsSub = obs;
    this._validatorsSub.subscribe((validatorFns) => {
      this._validators = validatorFns;
      this.updateFormValidation();
    });
  }
  public get validators(): Observable<Array<ValidatorFn>> {
    return this._validatorsSub;
  }

  @Output()
  public save = new EventEmitter<string>();
  @Output()
  public isEditModeChange = new EventEmitter<boolean>();

  // It's not clear why the autoFocus directive is not working
  // in this case. This essentially duplicates that directive
  // in this component.
  @ViewChild('description', { static: false })
  public set descriptionField(value: ElementRef) {
    setTimeout(() => {
      if (value) {
        value.nativeElement.focus();
      }
    }, 250);
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit() {
    this.isEditMode = false;
  }

  onSaveClicked() {
    let newDescription = this.editForm.get('description').value?.trim();
    this.save.emit(newDescription);
  }

  onDeleteClicked() {
    let comp = this;

    comp.confirmationDialogRef = comp.dialog.open(ModalConfirmationComponent, {
      viewContainerRef: comp.viewContainerRef,
      data: { action: `delete this ${comp.label}`, delete: true },
    });
    comp.confirmationDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.save.emit(null);
      }
      comp.confirmationDialogRef = null;
    });
  }

  isValid(): boolean {
    return this.editForm && this.editForm.valid;
  }

  allowCreate(): boolean {
    return !this.readOnly && !this.description && this.showCreateInReadMode;
  }

  allowDelete(): boolean {
    return !this.readOnly && this.showDeleteInReadMode;
  }

  toggleEditMode(isEditMode: boolean) {
    this.isEditMode = isEditMode;
    if (this.isEditMode) {
      let validators = new Array<ValidatorFn>();
      if (!this.subText) {
        validators.push(Validators.required);
      }
      validators.push(...this._validators);
      this.editForm = this.formBuilder.group({
        description: [this.description, validators],
      });
    }
    this.isEditModeChange.emit(isEditMode);
  }

  private updateFormValidation() {
    if (!this.isEditMode || !this.editForm) return;

    let descriptionForm = this.editForm.get('description');
    descriptionForm.clearValidators();

    let validators = new Array<ValidatorFn>();
    if (!this.subText) {
      validators.push(Validators.required);
    }
    validators.push(...this._validators);

    descriptionForm.setValidators(validators);
    descriptionForm.updateValueAndValidity();
  }
}
